import React, { useRef, useCallback, useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react'
import { useField, useFormikContext } from 'formik'

import { Template } from 'devextreme-react/core/template'
import { DropDownBox, DropDownBoxRef, FieldAddons } from 'devextreme-react/drop-down-box'
import DataGrid, { DataGridRef, Selection, Scrolling, Paging, FilterRow, SearchPanel } from 'devextreme-react/data-grid'
import LoadIndicator from 'devextreme-react/load-indicator'
import { IPopupOptions } from 'devextreme-react/popup'
import { useLabelWidth } from 'hooks/use-label-width'
import { useTranslation } from 'react-i18next'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

import { v4 as uuid } from 'uuid'

import '../index.scss'
import { FormErrors } from 'components/forms/form-errors'
import { useIxLookupContext } from 'contexts/lookup-context'
import { FilterMap } from 'hooks/lookup-field-hooks/use-filters-list'
import { LoadFn, ResetFn } from 'hooks/lookup-field-hooks/use-lookup-fetch'
import { useHandleKbEventsCallback } from 'hooks'
import { usePrevious } from 'helpers/use-previous'
import { shallowEqual } from 'helpers/shallow-equal'
import { isEqual } from 'lodash'

import { useSelector } from 'react-redux'
import { RootState } from 'slices'
import { Button } from 'devextreme-react/button'
import useResizeObserver from '@react-hook/resize-observer'

import { getIconClass } from 'helpers/get-icon-class'

import './base-lookup.scss'

interface IBaseLookupProps {
    name: string,
    label: string,
    opts: Array<any>,
    valueExpr?: string,
    keyExpr?: string,  // used for internally managing selected object, usually using HDR_ID
    displayExpr?: (string | ((val: any) => string)),
    inputExpr?: (string | ((val: any) => string)),
    loading?: boolean,
    customizeColumns?: (cols: Array<any>) => void,
    readOnly?: boolean,
    visible?: boolean,
    noDataText?: string,
    // searchText?: string,

    onInputChanged: (value: any) => void,
    onSelectionChanged: (value: any, rangeStart?: string) => void,

    value: any | null,
    callbacks: { [k: string]: (value: any, force?: boolean) => void }

    // stuff for loading data, provided by the parent
    contextFilters: any,
    loadFn: LoadFn,
    resetFn: ResetFn,

    matchContextFilter?: boolean,
    columns?: any[],

    selectionMode?: 'single' | 'multi' | 'multicsv' | 'range' | 'singlerange',
    openDropdown?: any,
    onInfoChange?: any,
    shouldUpdate?: any,
    getFirstCtxValue?: boolean,
    dataLoaded: boolean,

    placeholder?: string,
    filters?: any,
    buttons?: any[],

    iconsMap?: any,
    iconField?: string,

    cellRenderTemplates?: any[],
}

export const BaseLookup = forwardRef((
    {
        name,
        label,
        opts,
        valueExpr='value',
        keyExpr='HDR_ID',
        displayExpr='name',
        inputExpr='name',
        loading=false,
        customizeColumns,
        readOnly=false,
        visible=true,
        onInputChanged,
        onSelectionChanged,
        value,
        callbacks,
        contextFilters,
        loadFn,
        resetFn,
        // matchContextFilter=true,
        columns,
        noDataText,
        selectionMode='single',
        openDropdown,
        onInfoChange,
        shouldUpdate,
        getFirstCtxValue,
        dataLoaded,
        placeholder,
        filters,
        buttons=[],
        iconsMap,
        iconField,
        cellRenderTemplates=[],
    }: IBaseLookupProps,
    ref
) => {
    const { setFieldValue: setFormikFieldValue, setStatus, status } = useFormikContext()
    const [field, meta] = useField(name)
    const { t } = useTranslation()
    const dropdownbox = useRef<DropDownBoxRef>(null)
    const popup = useRef<any>(null)
    const grid = useRef<DataGridRef>(null)
    // const [isLoading, setIsLoading] = useState(false)
    const prevValue = usePrevious(value)
    const prevContextFilters = useRef<FilterMap>({ ...contextFilters.state })
    const [searchText, setSearchText] = useState('')
    const [inFocus, setInFocus] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    const [inputId] = useState(uuid())
    const {value: lookupThreshold} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupThreshold))
    const {value: dropDownHeight} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.dropDownHeight))
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    const {value: openLookupOnClick} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.openLookupOnClick))


    const [fieldWidth, setFieldWidth] = useState<any>(null)
    const prevOpenDropdown = useRef(openDropdown)
    const [rangeStart, setRangeStart] = useState<string>('')
    const getLabelWidth = useLabelWidth(formFieldType)
    const additionalButtons = useRef(buttons)

    // test
    const [selectedRows, setSelectedRows] = useState<any[]>([])

    useImperativeHandle(ref, () => {
        return {
            open: (searchText: string | null, rangeStart?: string) => {
                if (rangeStart) { setRangeStart(rangeStart) }
                else if (searchText) {
                    const currentGrid = grid.current
                    setTimeout(() => { currentGrid?.instance()?.searchByText(searchText) }, 0)
                    setSearchText(searchText)
                }
            },
            component: () => {
                return dropdownbox.current?.instance()?.element
            }
        }
    }, [])

    const {
        addField, addFieldChangeCallback, removeFieldChangeCallback, setFieldValue: setIxLookupFieldValue,
        getFieldValue: getLookupFieldValue, getFieldSelectionMode,
    } = useIxLookupContext()

    const setFieldValue = useCallback((field: string, value: any, shouldValidate = true) => {
        // console.log('BaseLookup: setFieldValue of', field, ' to ', value)
        setFormikFieldValue(field, value, shouldValidate)
    }, [setFormikFieldValue])

    const setLookupFieldValue = useCallback((name: string, value: string | number | null, fromSelf?: boolean, force?: boolean) => {
        // console.log('BaseLookup: setLookupFieldValue of ', name, ' to ', value)
        setIxLookupFieldValue(name, value, fromSelf, force)
    }, [setIxLookupFieldValue])

    const inputAttr = useMemo(() => (
        {
            name: field.name,
            id: `lookup-${inputId}`,
            style: `cursor: ${openLookupOnClick ? 'pointer' : 'text'}`
        }
    ), [field.name, inputId, openLookupOnClick])

    // const fieldRender = useFieldRenderCallback(value, displayExpr, inputExpr, inputAttr)

    const onInput = useCallback((e: any) => {
        if (selectionMode === 'singlerange') {
            const forbidden = [',', '-']
            const input = e.event.target as HTMLInputElement

            forbidden.forEach((symbol: any) => {
                input.value = input.value.replace(symbol, '')
            })
        }
        
        if (status === undefined) {
            setStatus('changing')
        }
    }, [setStatus, status, selectionMode])

    const onFocusIn = useCallback(() => setInFocus(true), [])
    const onFocusOut = useCallback(() => setInFocus(false), [])

    const isFloatingLabel = useMemo(() => {
        return formFieldType === 'floating-label' || formFieldType === 'floating-label-compact'
    },[formFieldType])

    const logWidth = useCallback(() => {
        const refDropdownbox: any = dropdownbox.current
        const input: any = refDropdownbox?.instance()?.element().querySelector('.dx-texteditor-input-container')

        input && setFieldWidth(input.getBoundingClientRect().width)
    },[])

   const onResize = useCallback((e: any) => {
        isFloatingLabel && setFieldWidth(e.contentRect.width)
    },[isFloatingLabel])

    useResizeObserver((dropdownbox.current?.instance()?.element().querySelector('.dx-texteditor-input-container') as any), onResize)

    useEffect(() => {
        if (isFloatingLabel) {
            logWidth()
        }
    },[logWidth, value, isFloatingLabel])

    useEffect(() => {
        if (isFloatingLabel) {
            const refDropdownbox: any = dropdownbox.current
            const floatingLabel: HTMLElement = refDropdownbox?.instance()?.element().querySelector('.dx-label')
            if (value) {
                const focusedFloatingLabel: HTMLElement = refDropdownbox?.instance()?.element().querySelector('.dx-texteditor-label .dx-label')

                floatingLabel && focusedFloatingLabel && (focusedFloatingLabel.style.left = `calc(100% - 60px - ${buttons.length ? buttons.length * 38 : 0}px - ${floatingLabel.offsetWidth-12}px)`)

                if (floatingLabel) {
                    const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                    const floatingLabelWidth = getLabelWidth(label, fontFamily)
                    const input: any = refDropdownbox?.instance()?.element().querySelector('input.dx-texteditor-input')

                    fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+26)}px`)
                }

            }
            else {
                if (floatingLabel) {
                    const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                    const floatingLabelWidth = getLabelWidth(label, fontFamily)
                    const input: any = refDropdownbox?.instance()?.element().querySelector('input.dx-texteditor-input')

                    floatingLabel.style.left = inFocus || isOpen ? `calc(100% - 32px - ${buttons.length ? buttons.length * 38 : 0}px - ${floatingLabel.offsetWidth-12}px)` : '0px'
                    fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+16)}px`)
                }
            }
        }

    },[buttons, value, getLabelWidth, label, fieldWidth, inFocus, isOpen, isFloatingLabel])


    // there are some cases where chaning inFocus makes the dropdownbox rerender and it loses focus when it should not
    // this makes sure inFocus and the actual state of the dropdownbox are matched

    const valueFormatter = useCallback((val: any) => {
        if (!val) { return '' }

        if (typeof(val) === 'string') { return '' }

        if (typeof(val) === 'number') {

            let newVal = opts.find((o: any) => o[valueExpr] === val)

            if (!newVal){
                // console.log('BaseLookup: value', val, 'of field', name, 'not found in dataSource,', opts)
                return ''
            }
            // console.log('BaseLookup: newVal of field', name, 'found', val, '=>', newVal, ', dataSource:', opts)
            val = newVal;
        }

        if (inFocus) {
            return (typeof(inputExpr) === 'function')
                ? inputExpr(val)
                : (val[inputExpr] ? val[inputExpr].toString() : '')
        } else {
            return (typeof(displayExpr) === 'function')
                ? displayExpr(val)
                : (val[displayExpr] ? val[displayExpr].toString() : '')
        }
    }, [inFocus, inputExpr, displayExpr, opts, name, valueExpr])

    const handleChange = useCallback((e: any) => {
        if (!e.event) { return }
        onInputChanged(e.value)
        sessionStorage.setItem('fieldValue', '')
        if (e.value) {
            setTimeout(() => setInFocus(false), 0)
        }

        setStatus(undefined)
    }, [onInputChanged, setStatus])

    const handleBlur = useCallback((e: any) => {
        setStatus(undefined)
        if (isOpen) return

        onFocusOut()
        const onBlur = field.onBlur
        onBlur({ target: { name } })
    }, [field.onBlur, onFocusOut, isOpen, setStatus, name])

    const handlePopupInitialized = useCallback((e: any) => {
        popup.current = e.component
    }, [])

    /* v8 ignore start */
    const handlePopupShowing = useCallback((e: any) => {
        (!visible || opts.length > lookupThreshold) && e.component.content().parentNode.classList.add('dropdownbox-input-overlay')

        setTimeout(() => popup.current?.repaint(), 100)
    }, [opts.length, lookupThreshold, visible])
    /* v8 ignore end */

    const handlePopupShown = useCallback(() => {
        const currentGrid = grid.current
        if (value && selectionMode !== 'range' && selectionMode !== 'singlerange') {
            // update selectedRows state in case the value was changed while the lookup was closed
            const selectedRows = Array.isArray(value) ? value.map((x: any) => x[keyExpr]) : [value[keyExpr]]
            setSelectedRows(selectedRows)
            // if we have any selected rows navigate to the first one
            selectedRows.length && currentGrid?.instance()?.navigateToRow(selectedRows[0])
        } else {
            setSelectedRows([])
        }

    }, [keyExpr, value, selectionMode])

    /* v8 ignore start */
    const handlePopupHiding = useCallback((e: any) => {
        onInfoChange && onInfoChange({isVisible: false, selectedRow: false})
        dropdownbox.current?.instance()?.close()
    }, [onInfoChange])
    /* v8 ignore end */

    const handlePopupHidden = useCallback(() => {
        setStatus(undefined)

        // in range mode we don't show selected rows so clear them after closing the popup
        if (selectionMode === 'range' || selectionMode === 'singlerange') {
            setSelectedRows([])
            grid.current?.instance()?.deselectAll()
        }

        // clear search text
        setSearchText('')
        setRangeStart('')
        grid.current?.instance()?.searchByText('')
    }, [setStatus, selectionMode])

    const _customizeColumns = useCallback((columns: any[]) => {
        columns.forEach(col => {
            // hide these by default for all models
            if(col.dataField === 'HDR_ID') { col.visible = false }
            if(col.dataField === 'HDR_H1_USERID') { col.visible = false }
            if(col.dataField === 'HDR_H1_TIMESTAMP') { col.visible = false }
            if(col.dataField === 'HDR_H2_USERID') { col.visible = false }
            if(col.dataField === 'HDR_H2_TIMESTAMP') { col.visible = false }
        })

        if (customizeColumns) {
            customizeColumns(columns)
        }
    }, [customizeColumns])

    /* v8 ignore start */
    const handleRowClick = useCallback((e: any) => {
        setInFocus(false)
        // in handleGridReady the visible row is passed in the event in case the grid is not fully initialized yet and we cannot get the selected rows data from the grid instance
        const rowsData = (e.visibleRows) ? e.visibleRows.map((r: any) => r.data) : grid.current?.instance()?.getSelectedRowsData()
        dropdownbox.current?.instance()?.close()
        
        if (onInfoChange && rowsData?.length)  { onInfoChange({isVisible: false, selectedRow: rowsData[0]}) }

        if (isFloatingLabel) {
            if (!value && !rowsData?.length) {
                const refDropdownbox: any = dropdownbox.current
                const floatingLabel: HTMLElement = refDropdownbox?.instance()?.element()
                .parentElement.querySelector('.dx-editor-filled.dx-texteditor-empty.dx-texteditor-with-floating-label .dx-texteditor-label .dx-label')
                floatingLabel && (floatingLabel.style.left = '0px')
            }
        }

        let orderedData = rowsData?.length === 1 ? rowsData : []
        // order the selected items according to selectedRows state
        // (devextreme messes the order in some cases otherwise)
        if (rowsData && rowsData.length > 1 && selectedRows) {
            orderedData = selectedRows.map((rId: any) => rowsData.find((r: any) => r[keyExpr] === rId))

            if(e.data && !selectedRows.find((rId: any) => rId === e.data.HDR_ID)) {
                orderedData.push(e.data)
            }
        }

        if (selectionMode === 'single') {
            if (orderedData.length === 1) {
                const row = orderedData[0]
                if (!value || row?.HDR_ID !== value[keyExpr]) {
                    onSelectionChanged(row)
                }
            } else {
                onSelectionChanged(null)
            }
        } else {
            if (orderedData.length === 0) {
                selectionMode !== 'range' && onSelectionChanged(null)
            } else {
                const getKeyExpr = (x: any) => x[keyExpr]
                // if (selectionMode === 'range' || (!value || !shallowEqual(value.map(getKeyExpr), orderedData.map(getKeyExpr)))) {
                if (selectionMode === 'range' || selectionMode === 'singlerange' || (!value || !shallowEqual(value.map(getKeyExpr), orderedData.map(getKeyExpr)))) {
                    onSelectionChanged(orderedData)
                }
            }
        }
    }, [dropdownbox, keyExpr, onSelectionChanged, value, selectionMode, selectedRows, onInfoChange, isFloatingLabel, name])
    /* v8 ignore end */

    const handleGridReady = useCallback((e: any) => {
        const visibleRows = e.component.getVisibleRows()

        if (visibleRows.length === 1) {
            setTimeout(() => {
                selectionMode === 'multi'
                    ? setSelectedRows((selectedRows: any) => [...selectedRows, visibleRows[0]?.data.HDR_ID])
                    : setSelectedRows([visibleRows[0]?.data.HDR_ID])
            }, 10)

            if (!isOpen) {
                grid.current?.instance()?.clearFilter()
                setSearchText('')
                setTimeout(() => {handleRowClick({data: null, visibleRows: visibleRows})}, 100)
            }
        }
        else {
            if (searchText) {
                dropdownbox.current?.instance()?.open()
                e.element.querySelector('.dx-icon.dx-icon-search + input')?.focus()
            }
        }

    }, [handleRowClick, searchText, isOpen, selectionMode])

    // special handler for lookups in dropdown mode (when opts is below lookupThreshold)
    // we need to discuss how this mode works with multiselect??
    const handleRowClickDropdownMode = useCallback(() => {
        setInFocus(false)
        const rowsData = grid.current?.instance()?.getSelectedRowsData()
        dropdownbox.current?.instance()?.close()

        if (rowsData) {
            onInfoChange && onInfoChange({isVisible: false, selectedRow: rowsData[0]})
            if (rowsData.length === 1) {
                const row = rowsData[0]
                if (!value || row.HDR_ID !== value[keyExpr]) {
                    onSelectionChanged(row, rangeStart)
                }
            } else {
                onSelectionChanged(null)
            }
        }
    }, [keyExpr, onSelectionChanged, value, onInfoChange, rangeStart])

    const selectRow = useCallback((e: any) => {
        grid.current?.instance()?.selectRows([e.key], true)
    },[])

    const _onSelectionChanged = useCallback((e: any) => {
        setSelectedRows((selectedRows: any[]) => {
            let _selectedRows = selectedRows.filter((r: any) => e.currentDeselectedRowKeys.indexOf(r) === -1)
            if (!isEqual(_selectedRows, e.currentSelectedRowKeys)) {
                _selectedRows = _selectedRows.concat(e.currentSelectedRowKeys)
            }
            return _selectedRows
        })
    }, [])

    const dropDownOptions: IPopupOptions = useMemo(() => {
        return {
            maxHeight: !visible || opts.length > lookupThreshold ? '600px' : dropDownHeight,
            onInitialized: handlePopupInitialized,
            position: !visible || opts.length > lookupThreshold ? { my: 'center', at: 'center', of: window } : { at: 'left bottom', my: 'left top', collision: 'flipfit', of: `#lookup-${inputId}`},
            toolbarItems: !visible || opts.length > lookupThreshold ? [
                {
                    toolbar: 'top',
                    location: 'after',
                    widget: 'dxButton',
                    disabled: false,
                    options: {
                        type: 'close',
                        icon: 'close',
                        onClick: () => {
                            onInfoChange && onInfoChange({isVisible: false, selectedRow: false})
                            dropdownbox.current?.instance()?.close()
                            dropdownbox.current?.instance()?.focus()
                        }
                    }
                }
            ] : [],
            dragEnabled: true,
            onShowing: handlePopupShowing,
            onShown: handlePopupShown,
            onHiding: handlePopupHiding,
            onHidden: handlePopupHidden,
            title: !visible || opts.length > lookupThreshold ? label || name : '',
            showTitle: (!visible || opts.length > lookupThreshold) && true,
            hideOnOutsideClick: (e: any) => { return opts.length <= lookupThreshold && !dropdownbox.current?.instance()?.element().contains(e.target) },
                minWidth: !visible || opts.length > lookupThreshold ? '600px' : '300px',
            deferRendering: false,
        }
    }, [handlePopupInitialized, handlePopupShowing, handlePopupHiding, label, name, opts.length, dropDownHeight,
        lookupThreshold, inputId, onInfoChange, visible, handlePopupHidden, handlePopupShown])

    // handle arrow keys in grid
    // this is in custom hook to enable some sort of testing despite the ref used
    const handleKbEventsCallback = useHandleKbEventsCallback(grid.current, dropdownbox.current, opts)
    useEffect(() => {
        const currentGrid = grid.current?.instance()
        selectionMode === 'single' && currentGrid?.element().addEventListener('keyup', handleKbEventsCallback)
        return () => currentGrid?.element().removeEventListener('keyup', handleKbEventsCallback)
    }, [grid, handleKbEventsCallback, selectionMode])

    useEffect(() => {
        addField(name, null, readOnly, selectionMode, valueExpr)
    }, [addField, name, readOnly, selectionMode, valueExpr])

    // register field in lookup context and update it on value change
    useEffect(() => {
        // const val = getLookupFieldValue(name)
        // if (val === undefined) {
        //     addField(name, value, readOnly, selectionMode)
        // }

        // if everything is the same do nothing
        if (value !== prevValue && prevValue !== undefined) {
            const val = getLookupFieldValue(name)
            if (
                (val === null && value === null) ||
                (selectionMode === 'range' && val === value) ||
                (selectionMode !== 'range' && !Array.isArray(value) && (val && value && val[keyExpr] === value[keyExpr])) ||
                (selectionMode !== 'range' && Array.isArray(value) && (val && value && isEqual(val.map((x: any) => x[keyExpr]), value)))
            ) {
                return
            }

            // only update formik context if we're not readOnly field
            let newValue: any = null
            if (Array.isArray(value)) {
                newValue = value.map((x: any) => x[valueExpr])
                if (selectionMode === 'multicsv') {
                    newValue = newValue.join(', ')
                }
            } else if (value) {
                // if (selectionMode === 'range') {
                if (selectionMode === 'range' || selectionMode === 'singlerange') {

                    newValue = value
                } else {
                    newValue = value[valueExpr]
                }
            }

            !readOnly && setFieldValue(name, newValue, false)

            // update global fields if any
            let row = null
            if (Array.isArray(value) && value.length) { row = value[0] }
            else if (!Array.isArray(value) && value) { row = value }

            if (row && selectionMode !== 'range') {
                if (meta.touched) { // only update related lookups if it user change that triggered this
                    for (let fd of shouldUpdate) {
                        const gFieldSelectionMode= getFieldSelectionMode(fd.fieldName)
                        if (gFieldSelectionMode === 'range') { continue }
                        let val = getLookupFieldValue(fd.fieldName)
                        if (val === undefined) { continue }
                        if (Array.isArray(val)) {
                            if (getFirstCtxValue) {
                                val = val.length ? val[0] : null
                            } else {
                                continue
                            }
                        }

                        if (val instanceof Object) { val = val[fd.valueExpr] }

                        const newVal = row[fd.localField ? fd.localField : fd.valueExpr]
                        if (!val || newVal !== val) {
                            setLookupFieldValue(fd.fieldName, newVal)
                        }
                    }
                }
            }

            // always update lookup context
            setLookupFieldValue(name, value, true)
        }
    }, [value, prevValue, addField, setFieldValue, setLookupFieldValue, getLookupFieldValue,
        name, valueExpr, keyExpr, readOnly, shouldUpdate, getFirstCtxValue, selectionMode,
        getFieldSelectionMode, meta.touched])

    // register callbacks in lookup context
    useEffect(() => {
        if (!callbacks) { return }

        Object.keys(callbacks).forEach(fieldName => {
            addFieldChangeCallback(fieldName, callbacks[fieldName], name === fieldName, name)
        })

        return () => {
            Object.keys(callbacks).forEach(fieldName => {
                removeFieldChangeCallback(fieldName, callbacks[fieldName], name)
            })
        }
    }, [name, callbacks, addFieldChangeCallback, removeFieldChangeCallback])

    // on filter change check and reset dataset if needed
    useEffect(() => {
        if (!shallowEqual(contextFilters.state, prevContextFilters.current) && !readOnly) {
            const prevCtxFilters = { ...prevContextFilters.current }
            prevContextFilters.current = ({ ...contextFilters.state })
            let resetedFlag = false

            // if (Object.keys(contextFilters.state).length === 0 || (!value && value !== prevValue) || (Array.isArray(value) && value.length === 0)) {
            if (Object.keys(contextFilters.state).length === 0 || !value || (Array.isArray(value) && value.length === 0)) {
                selectionMode !== 'range' && onSelectionChanged(null)
                // console.log('BaseLookup: context filters were reseted, resetting value of ', name)
                resetFn()
                return
            }

            // if (!value) { return }

            for (let filter in contextFilters.state) {
                // when in multiselect we use only the first selection's global filters
                let val = value
                if (Array.isArray(value)) { val = value[0] }
                if ((val.hasOwnProperty(filter.toUpperCase()) && contextFilters.state[filter] !== val[filter.toUpperCase()])
                    // when the context filter is just a filter field, not a column in the value, then the values were reseted after tab change.
                    // after tab change the prevCtxFilters are empty and we will check if it contains the filter.
                    // if the filter is not in value and not in prevCtxFilters then we can assume that the value is still correct => check if prevCtxFilters.hasOwnProperty(filter.toUpperCase())
                    || (!val.hasOwnProperty(filter.toUpperCase()) && prevCtxFilters.hasOwnProperty(filter.toUpperCase()) && contextFilters.state[filter] !== prevCtxFilters[filter])) {
                    selectionMode !== 'range' && onSelectionChanged(null)

                    // console.log('BaseLookup: context filter', filter, ' was changed, resetting value of ', name)
                    resetFn()
                    resetedFlag = true
                    break;
                }
            }

            if (!resetedFlag && value) {
                if (Array.isArray(value)) {
                    resetFn(value.map((v: any) => v.HDR_ID))
                } else {
                    resetFn([value.HDR_ID])
                }
            }
        }
    }, [name, contextFilters, onSelectionChanged, value, prevValue, resetFn, readOnly, selectionMode])

    const onGridOpened = useCallback((e: any) => {
        if(e.name === 'opened') {
            setIsOpen(e.value)
        }
    }, [])

    const onOpenButton = useCallback(() => {
        const input: any = dropdownbox.current?.instance()?.element().querySelector(`#lookup-${inputId}`)
        const currentGrid = grid.current

        if (!dataLoaded) {
            (selectionMode !== 'range' && selectionMode !== 'singlerange' && input.value) && currentGrid?.instance()?.searchByText(input.value)

            loadFn(contextFilters.state/* , {}, false */)
                .then(() => {
                    setIsOpen(true)
                    setTimeout(() => dropdownbox.current?.instance()?.open(), 100)
                })
                .catch(() => {})
        } else {
            (input.value && !value) && currentGrid?.instance()?.searchByText(input.value)
            setTimeout(() => dropdownbox.current?.instance()?.open(), 100)
        }
    }, [dataLoaded, contextFilters.state, loadFn, inputId, selectionMode, value])

    const onCloseButton = useCallback(() => { dropdownbox.current?.instance()?.close() }, [])

    const ddButtons = useMemo<any>(() => ([
        { name: 'clear' },
        ...additionalButtons.current,
        {
            location: 'after',
            name: 'dropdown',
            options: {
                icon: isOpen ? 'spinup' : 'spindown',
                onClick: isOpen ? onCloseButton : onOpenButton,
                elementAttr: {
                    class: 'dropdown-btn'
                }
            }

        }
    ]), [onOpenButton, onCloseButton, isOpen])

    useEffect(() => {
        const input: any = dropdownbox.current?.instance()?.element().querySelector(`#lookup-${inputId}`)

        if (input) {
            if (openLookupOnClick) {
                input.addEventListener('click', onOpenButton)
            }

            return () => {
                input.removeEventListener('click', onOpenButton)
            }
        }

    },[openLookupOnClick, inputId, onOpenButton])

    useEffect(() => {
        if (openDropdown === prevOpenDropdown.current) { return }
        prevOpenDropdown.current = openDropdown

        openDropdown ? onOpenButton() : dropdownbox.current?.instance()?.close()
        setIsOpen(openDropdown)
    },[openDropdown, onOpenButton, name])

    useEffect(() => {
        if (isOpen) {
            sessionStorage.setItem('fieldValue', '')
        }
    }, [isOpen])

    const handleKeyUp = useCallback((e: any) => {
        if (e.event.key.length === 1 || e.event.key === 'Backspace') {
            sessionStorage.setItem('fieldValue', e.event.currentTarget.value)
        }
    },[])

    const _value = useMemo(() => {
        if (selectionMode === 'range' || selectionMode === 'singlerange') {
            return value
        } else {
            return value && (Array.isArray(value) ? value.map((x: any) => x[keyExpr]) : value[keyExpr])
        }
    }, [value, keyExpr, selectionMode])

    const beforeRender = useCallback(() => {
        // if (!value || !iconsMap || !iconField || !iconsMap[value[iconField]] || selectionMode !== 'single') { return null }
        const iconDef = (value && iconField && value[iconField]) ? iconsMap[value[iconField]] : null
        return (
            <div style={ { display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', height: '100%' } }>
                <Button
                    elementAttr={ { class: 'label-inside-field' } }
                    stylingMode='text'
                    text={ label }
                    hoverStateEnabled={ false }
                    focusStateEnabled={ false }
                    activeStateEnabled={ false }
                />
                {
                    (!value || !iconsMap || !iconField || !iconsMap[value[iconField]] || selectionMode !== 'single')
                        ? null
                        : (
                            <div className='ix-input-icon-container'>
                                <i className={`ix-input-icon ${getIconClass(iconDef.icon)}`} style={ iconDef.color ? { color: iconDef.color } : {} }></i>
                            </div>
                        )
                }
            </div>
        )
    }, [iconsMap, iconField, label, selectionMode, value])

    return (
        <div className={`rb-item mb-1 ${ visible ? '' : 'hidden'}`}>
            <div>
                <label htmlFor={field.name} className='dx-field-label'>{label}</label>
                <div className='' style={ loading ? {} : { display: 'none' } }>
                    <LoadIndicator height={16} width={16} />
                </div>
                <DropDownBox
                    label={label}
                    labelMode= 'floating'
                    ref={dropdownbox}
                    className={ `dx-field-input${ loading ? ' hidden' : '' }` }
                    name={field.name}
                    buttons={ddButtons}
                    inputAttr={inputAttr}
                    value={_value}
                    { ...(selectionMode === 'range' || selectionMode === 'singlerange' ? {} : { valueExpr: keyExpr, displayExpr: valueFormatter }) }
                    // valueExpr={keyExpr}
                    // opened={open}
                    placeholder={placeholder || t('global:label.select')}
                    dataSource={opts}
                    deferRendering={false}
                    dropDownOptions={dropDownOptions}
                    onValueChanged={handleChange}
                    onFocusIn={onFocusIn}
                    onFocusOut={handleBlur}
                    isValid={!(meta.touched && meta.error)}
                    showClearButton={value !== null}
                    readOnly={readOnly}
                    openOnFieldClick={false}
                    acceptCustomValue={selectionMode !== 'multi' && selectionMode !== 'multicsv'}
                    // displayExpr={valueFormatter}
                    onOptionChanged={onGridOpened}
                    onKeyUp={handleKeyUp}
                    onInput={onInput}
                >
                    <FieldAddons beforeRender={ beforeRender }/>
                    <div style={ { display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {
                            filters &&
                            <div>
                               { filters()}
                            </div>
                        }
                        <div style={ { display: 'flex', overflow: 'auto' }}>
                            <DataGrid
                                id='dropdown-grid'
                                ref={grid}
                                dataSource={opts}
                                height='100%'
                                onContentReady={handleGridReady}
                                customizeColumns={_customizeColumns}
                                keyExpr={keyExpr}
                                showBorders={true}
                                showColumnLines={true}
                                {...noDataText && {noDataText: noDataText}}
                                {...(!visible || (!rangeStart && (opts.length > lookupThreshold || selectionMode !== 'single')))
                                    ? {onRowDblClick: handleRowClick, onRowClick: selectRow}
                                    : {onRowClick: handleRowClickDropdownMode}}
                                columns={columns}
                                columnAutoWidth={true}
                                width='100%'
                                onSelectionChanged={ _onSelectionChanged }
                                { ...(selectionMode !== 'range' && selectionMode !== 'singlerange' ? { selectedRowKeys: selectedRows } : {}) }
                                // selectedRowKeys={ selectedRows }
                            >
                                <Selection mode={ (selectionMode === 'single' || selectionMode === 'singlerange' || rangeStart) ? 'single' : 'multiple' } deferred={false} showCheckBoxesMode={ selectionMode !== 'single' && selectionMode !== 'singlerange' ? 'always' : 'none' } />
                                <Scrolling mode='virtual' scrollByContent={true} />
                                <Paging enabled={true} pageSize={10} />
                                <FilterRow visible={true} />
                                {/* {opts.length > 10 && <SearchPanel searchVisibleColumnsOnly={true} visible={true} text={searchText} />} */}
                                <SearchPanel searchVisibleColumnsOnly={true} visible={true} /*defaultText={ searchText }*/ />
                                { cellRenderTemplates.map((item) => {
                                    return (<Template key={item.name} name={item.name} render={item.render} />)
                                }) }
                            </DataGrid>
                        </div>

                        {
                            (!visible || opts.length > lookupThreshold || selectionMode !== 'single') &&
                            <div className='confirm-popup--btns' style={{ flexBasis: 'auto' }}>
                                <Button text={t('global:button.ok')} type='default' onClick={handleRowClick} />
                                <Button text={t('global:button.cancel')} type='default' onClick={() => {
                                    dropdownbox.current?.instance()?.close()
                                    setTimeout(() => dropdownbox.current?.instance()?.focus(), 0)
                                    }} />
                            </div>
                        }

                    </div>
                </DropDownBox>
            </div>
            <FormErrors meta={meta} />
        </div>
    )
})

